import { Box, Divider, Stack, Typography } from '@mui/material';
import React from 'react';
import type { WidgetProps } from '../types/index.js';
import { getProfileRatios } from '../../../services/profile.js';

type Ratios = NonNullable<Awaited<ReturnType<typeof getProfileRatios>>>;

const formatNumber = (value: number | null | undefined, decimals = 2): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'N/A';
  return value.toFixed(decimals);
};

const formatPercent = (value: number | null | undefined, decimals = 1): string => {
  const formatted = formatNumber(value, decimals);
  return formatted === 'N/A' ? formatted : `${formatted}%`;
};

export const KeyRatiosWidget: React.FC<WidgetProps> = ({ widget, onUpdateState }) => {
  const profileId = (widget.config as any)?.profileId as string | undefined;
  const ticker = (widget.inputs.find(i => i.id === 'ticker')?.value as string) || (widget.config as any)?.ticker as (string | undefined);
  const [ratios, setRatios] = React.useState<Ratios | null>(null);

  React.useEffect(() => {
    if (!profileId && !ticker) {
      onUpdateState?.({ loading: false, data: null });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const id = profileId || (ticker as string);
        const data = await getProfileRatios(id).catch(() => null);
        if (!cancelled) {
          setRatios(data);
          onUpdateState?.({ loading: false, data });
        }
      } catch {
        if (!cancelled) {
          setRatios(null);
          onUpdateState?.({ loading: false, data: null });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profileId, ticker, onUpdateState]);

  const items: Array<{ label: string; value: string }> = React.useMemo(() => {
    if (!ratios) return [];
    return [
      { label: 'P/E (TTM)', value: formatNumber(ratios.pe, 1) },
      { label: 'P/S (TTM)', value: formatNumber(ratios.ps, 1) },
      { label: 'EV/EBITDA', value: formatNumber(ratios.evToEbitda, 1) },
      { label: 'ROE', value: formatPercent(ratios.roe) },
      { label: 'ROIC', value: formatPercent(ratios.roic) },
      { label: 'Gross Margin', value: formatPercent(ratios.grossMargin) },
      { label: 'Operating Margin', value: formatPercent(ratios.operatingMargin) },
      { label: 'EBITDA Margin', value: formatPercent(ratios.ebitdaMargin) },
      { label: 'FCF Margin', value: formatPercent(ratios.fcfMargin) },
      { label: 'Current Ratio', value: formatNumber(ratios.currentRatio, 2) },
      { label: 'Quick Ratio', value: formatNumber(ratios.quickRatio, 2) },
      { label: 'Debt/Equity', value: formatNumber(ratios.debtToEquity, 2) },
    ];
  }, [ratios]);

  const columns = React.useMemo(() => {
    if (items.length === 0) return [] as Array<typeof items>;
    const columnCount = 3;
    const perColumn = Math.ceil(items.length / columnCount);
    return Array.from({ length: columnCount }, (_, idx) =>
      items.slice(idx * perColumn, (idx + 1) * perColumn)
    ).filter((group) => group.length > 0);
  }, [items]);

  if (!ratios) {
    return (
      <Box sx={{ p: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {profileId || ticker ? 'Loading ratios…' : 'Provide a profileId or ticker'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: `repeat(${Math.max(columns.length, 1)}, minmax(0, 1fr))`,
          },
          gap: 2,
        }}
      >
        {columns.map((group, columnIndex) => (
          <Stack
            key={columnIndex}
            divider={<Divider flexItem sx={{ borderColor: 'divider' }} />}
            spacing={1}
          >
            {group.map(({ label, value }) => (
              <Stack
                key={label}
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
                sx={{ py: 0.5, gap: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {value}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ))}
      </Box>
      {ratios.updatedAt && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 1.25, pt: 1 }}>
          Updated {new Date(ratios.updatedAt).toLocaleString()}
        </Typography>
      )}
    </Box>
  );
};
